import type { Comment } from '@/@types/comment'
import { type GetCommentParams, generateCommentQueryKey } from '@/utils/fetchers/comment'
import { useCurrentUser } from '@/utils/hooks/useUser'
import { css } from '@emotion/react'
import { BubbleIcon, LikeIcon, MoreIcon } from '@ruguoapp/jike-fe-icons'
import { useParams } from '@tanstack/react-router'
import { pick } from 'lodash-es'
import { type HTMLAttributes, useCallback } from 'react'
import toast from 'react-hot-toast'
import { default as ActionButton, type ActionButtonProps } from '../ActionButton'
import { BlurTextMode, FeaturableBlurTextSwitch } from '../BlurTextSwitch'
import DropdownMenu from '../DropdownMenu/DropdownMenu'
import type { MenuItemData } from '../DropdownMenu/types'
import { showConfirm } from '../Modal/utils'
import { ACTION_MENU_LOCALE, ActionMenu } from '../Post/config'
import { useCommentActionsContext } from './CommentActionsContext'
import { useCommentDelete, useCommentLikeToggle, useCommentPinToggle } from './hooks'

export interface CommentActionsProps extends HTMLAttributes<HTMLDivElement> {
	data: Comment
}

export default function CommentActions({ data, ...props }: CommentActionsProps) {
	const queryKey = generateCommentQueryKey(pick(data, ['id', 'targetType']) as GetCommentParams)
	const { toggleLike } = useCommentLikeToggle(queryKey)
	const { togglePin } = useCommentPinToggle(queryKey)
	const { deleteComment } = useCommentDelete(queryKey)
	const { id: postId, username } = useParams({
		strict: false,
		select: (params: Record<string, any>) => pick(params, ['id', 'username']),
	}) as {
		id: string | undefined
		username: string | undefined
	}
	const { refresh: refreshList, onComment } = useCommentActionsContext()

	const user = useCurrentUser()
	const getMoreActionsMenu = useCallback(
		(data: Comment | undefined) => {
			if (!data) {
				return []
			}
			const menu: MenuItemData[] = [ACTION_MENU_LOCALE[ActionMenu.COPY_LINK]]
			const isMyContent = data.user?.id === user.id

			if (isMyContent) {
				if (data.isPinned) {
					menu.push(ACTION_MENU_LOCALE[ActionMenu.UNPIN])
				} else {
					menu.push(ACTION_MENU_LOCALE[ActionMenu.PIN])
				}
			}

			if (isMyContent) {
				menu.push(ACTION_MENU_LOCALE[ActionMenu.DELETE])
			}

			return menu
		},
		[user.id]
	)

	const handleMoreActionMenuItemClick = useCallback(
		async (menuId: string) => {
			try {
				switch (menuId as ActionMenu) {
					case ActionMenu.COPY_LINK:
						{
							const username = data?.user?.username
							if (!username) return
							const url = new URL(
								`/u/${username}/post/${postId}/${data.id}`,
								window.location.origin
							)
							navigator.clipboard
								.writeText(url.toString())
								.then(() => {
									toast.success('复制成功')
								})
								.catch(() => {
									toast.error('复制失败')
								})
						}
						break
					case ActionMenu.PIN:
					case ActionMenu.UNPIN:
						await togglePin()
						await refreshList?.()
						break
					case ActionMenu.DELETE:
						await showConfirm(
							'确定删除吗？',
							<div
								css={css`
									text-align: center;
								`}
							>
								删除后无法恢复
							</div>
						)
						await deleteComment()
						break
				}
			} catch (e) {
				// toast.error((e as Error).message)
			}
		},
		[data, deleteComment, postId, refreshList, togglePin]
	)

	return (
		<div
			onClick={(e) => {
				e.stopPropagation()
			}}
			{...props}
			css={css`
				display: flex;
				color: var(--tint-secondary);
				font-size: 14px;

				.action-button {
					height: 20px;
				}
			`}
		>
			<ActionButton
				onClick={() => toggleLike()}
				icon={
					<LikeIcon
						width="20px"
						height="20px"
						css={
							data?.liked
								? css`
										color: var(--tint-error);
									`
								: css`
										transition: color 0.2s;
									`
						}
					/>
				}
			>
				<FeaturableBlurTextSwitch
					data={(data?.likeCount ?? 0).toString()}
					mode={BlurTextMode.SMALL}
				/>
			</ActionButton>

			<ActionButton
				onClick={() => {
					onComment({
						to: data,
						threadId: data?.threadId,
					})
				}}
				icon={<BubbleIcon width="20px" height="20px" css={css``} />}
			>
				<FeaturableBlurTextSwitch
					data={(data?.replyCount ?? 0).toString()}
					mode={BlurTextMode.SMALL}
				/>
			</ActionButton>

			<DropdownMenu
				data={getMoreActionsMenu(data)}
				onSelect={handleMoreActionMenuItemClick}
				position="bottom-end"
				renderTrigger={(triggerProps) => (
					<ActionButton
						icon={<MoreIcon width="20px" height="20px" css={css``} />}
						{...(triggerProps as ActionButtonProps)}
					/>
				)}
			/>
		</div>
	)
}
